import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useSSEStream } from './useSSEStream';

const DEBOUNCE_MS = 400;

interface UseTeamRealtimeOptions {
  organizationId: string | undefined;
  enabled?: boolean;
  getAccessToken?: () => Promise<string | null>;
}

export function useTeamRealtime({
  organizationId,
  enabled = true,
  getAccessToken,
}: UseTeamRealtimeOptions) {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMessage = useCallback(
    (_data: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['members', organizationId] });
        queryClient.invalidateQueries({ queryKey: ['invites', organizationId] });
        debounceTimerRef.current = null;
      }, DEBOUNCE_MS);
    },
    [queryClient, organizationId],
  );

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    },
    [],
  );

  const { isConnected } = useSSEStream({
    path: 'realtime/team/stream',
    organizationId,
    enabled,
    getAccessToken,
    onMessage: handleMessage,
  });

  return { isConnected };
}
